import { offlineStorageService, type PendingTransaction } from './offlineStorageService'
import { networkService } from './networkService'
import { cardRefuelService } from './cardRefuelService'
import { attendantRefuelService } from './attendantRefuelService'
import { subcontractorRefuelService } from './subcontractorRefuelService'
import { uploadVoucherPhoto, uploadSlipPhoto } from '@/utils/supabaseStorage'
import { ref, computed } from 'vue'

class SyncService {
    private isSyncing = ref(false)
    private lastSyncTime = ref<number | null>(null)
    private syncErrors = ref<string[]>([])
    private autoSyncEnabled = ref(true)
    private syncInterval: number | null = null

    constructor() {
        this.init()
    }

    private init() {
        // Listen for network status changes
        networkService.onStatusChange((status) => {
            if (status === 'online' && this.autoSyncEnabled.value) {
                console.log('[SyncService] Network is online, triggering auto-sync')
                this.syncPendingTransactions()
            }
        })

        // Set up periodic sync check (every 5 minutes when online)
        this.syncInterval = window.setInterval(() => {
            if (networkService.status.value === 'online' && this.autoSyncEnabled.value) {
                this.syncPendingTransactions()
            }
        }, 5 * 60 * 1000)
    }

    public get syncing() {
        return computed(() => this.isSyncing.value)
    }

    public get lastSync() {
        return computed(() => this.lastSyncTime.value)
    }

    public get errors() {
        return computed(() => this.syncErrors.value)
    }

    public async syncPendingTransactions(): Promise<void> {
        if (this.isSyncing.value) {
            console.log('[SyncService] Sync already in progress, skipping')
            return
        }

        if (networkService.status.value === 'offline') {
            console.log('[SyncService] Network is offline, skipping sync')
            return
        }

        this.isSyncing.value = true
        this.syncErrors.value = []

        try {
            const pendingTransactions = await offlineStorageService.getPendingTransactions()
            console.log(`[SyncService] Found ${pendingTransactions.length} pending transactions to sync`)

            if (pendingTransactions.length === 0) {
                this.lastSyncTime.value = Date.now()
                return
            }

            for (const transaction of pendingTransactions) {
                try {
                    await this.syncTransaction(transaction)
                } catch (error: any) {
                    console.error('[SyncService] Failed to sync transaction:', transaction.id, error)
                    this.syncErrors.value.push(`${transaction.type}: ${error.message}`)

                    // Update transaction status to failed
                    await offlineStorageService.updateTransactionStatus(
                        transaction.id,
                        'failed',
                        error.message
                    )

                    // Add to sync log
                    await offlineStorageService.addSyncLog(transaction.id, false, error.message)
                }
            }

            this.lastSyncTime.value = Date.now()

            // Clean up synced transactions older than 24 hours
            await this.cleanupOldTransactions()
        } finally {
            this.isSyncing.value = false
        }
    }

    private async syncTransaction(transaction: PendingTransaction): Promise<void> {
        console.log('[SyncService] Syncing transaction:', transaction.id, transaction.type)

        // Update status to syncing
        await offlineStorageService.updateTransactionStatus(transaction.id, 'syncing')

        try {
            // Handle photo uploads first if they exist
            if (transaction.photos) {
                await this.uploadPhotos(transaction)
            }

            // Sync based on transaction type
            switch (transaction.type) {
                case 'card_refuel':
                    await this.syncCardRefuel(transaction)
                    break
                case 'attendant_refuel':
                    await this.syncAttendantRefuel(transaction)
                    break
                case 'subcontractor_refuel':
                    await this.syncSubcontractorRefuel(transaction)
                    break
                case 'tank_refilling':
                    await this.syncTankRefilling(transaction)
                    break
                default:
                    throw new Error(`Unknown transaction type: ${transaction.type}`)
            }

            // Mark as synced
            await offlineStorageService.updateTransactionStatus(transaction.id, 'synced')
            await offlineStorageService.addSyncLog(transaction.id, true)

            console.log('[SyncService] Successfully synced transaction:', transaction.id)
        } catch (error) {
            throw error
        }
    }

    private async uploadPhotos(transaction: PendingTransaction): Promise<void> {
        const { photos, data } = transaction
        if (!photos) return

        const vehicleId = data.vehicle_id || 'unknown-vehicle'
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-')

        // Upload slip photo
        if (photos.slipPhoto) {
            const slipFile = new File([photos.slipPhoto], `slip_${timestamp}.jpg`, { type: 'image/jpeg' })
            const path = `${transaction.type}/${vehicleId}/${timestamp}_slip.jpg`
            data.slip_photo_url = await uploadSlipPhoto(slipFile, path)
        }

        // Upload voucher photo
        if (photos.voucherPhoto) {
            const voucherFile = new File([photos.voucherPhoto], `voucher_${timestamp}.jpg`, { type: 'image/jpeg' })
            const path = `${transaction.type}/${vehicleId}/${timestamp}_voucher.jpg`
            data.voucher_photo_url = await uploadVoucherPhoto(voucherFile, path)
        }

        // Upload odometer photos
        if (photos.odoBeforePhoto) {
            const odoBeforeFile = new File([photos.odoBeforePhoto], `odo_before_${timestamp}.jpg`, { type: 'image/jpeg' })
            const path = `${transaction.type}/${vehicleId}/${timestamp}_odo_before.jpg`
            data.odo_before_image_url = await uploadSlipPhoto(odoBeforeFile, path)
        }

        if (photos.odoAfterPhoto) {
            const odoAfterFile = new File([photos.odoAfterPhoto], `odo_after_${timestamp}.jpg`, { type: 'image/jpeg' })
            const path = `${transaction.type}/${vehicleId}/${timestamp}_odo_after.jpg`
            data.odo_after_image_url = await uploadSlipPhoto(odoAfterFile, path)
        }
    }

    private async syncCardRefuel(transaction: PendingTransaction): Promise<void> {
        await cardRefuelService.createCardRefuel(transaction.data)
    }

    private async syncAttendantRefuel(transaction: PendingTransaction): Promise<void> {
        await attendantRefuelService.create(transaction.data)
    }

    private async syncSubcontractorRefuel(transaction: PendingTransaction): Promise<void> {
        await subcontractorRefuelService.createRefuel(transaction.data)
    }

    private async syncTankRefilling(transaction: PendingTransaction): Promise<void> {
        // Import tankService functions dynamically to avoid circular dependencies
        const { addTankRefill } = await import('./tankService')
        await addTankRefill(transaction.data)
    }

    private async cleanupOldTransactions(): Promise<void> {
        const allTransactions = await offlineStorageService.getAllTransactions()
        const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000

        for (const tx of allTransactions) {
            if (tx.status === 'synced' && tx.timestamp < oneDayAgo) {
                await offlineStorageService.deleteTransaction(tx.id)
            }
        }
    }

    public async getPendingCount(): Promise<number> {
        return await offlineStorageService.getPendingCount()
    }

    public async retryFailedTransactions(): Promise<void> {
        const allTransactions = await offlineStorageService.getAllTransactions()
        const failedTransactions = allTransactions.filter(tx => tx.status === 'failed')

        for (const tx of failedTransactions) {
            // Reset to pending status
            await offlineStorageService.updateTransactionStatus(tx.id, 'pending')
        }

        // Trigger sync
        await this.syncPendingTransactions()
    }

    public setAutoSync(enabled: boolean): void {
        this.autoSyncEnabled.value = enabled
        console.log('[SyncService] Auto-sync', enabled ? 'enabled' : 'disabled')
    }

    public destroy(): void {
        if (this.syncInterval !== null) {
            clearInterval(this.syncInterval)
        }
    }
}

export const syncService = new SyncService()
